import { Tooltip } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { getWeekProgress } from '../../../../ultraPlanUtils';
import { getWeekElementId } from './RoadmapWeek';

const STICKY_TOOLBAR_HEIGHT = 50;
const ACTIVE_LINE_OFFSET = 120;
const SCROLL_SETTLE_DELAY = 150;

const getElement = (weekNumber) =>
  // eslint-disable-next-line no-undef
  document?.getElementById(getWeekElementId(weekNumber));

const RoadmapWeekNav = ({ weeks = [] }) => {
  const [activeWeekNumber, setActiveWeekNumber] = useState(
    weeks?.[0]?.weekNumber,
  );
  const navRef = useRef(null);
  const isClickScrollingRef = useRef(false);
  const settleTimerRef = useRef(null);
  const weekKey = weeks?.map((week) => week?.weekNumber)?.join(',');

  const releaseClickLock = () => {
    // eslint-disable-next-line no-undef
    window.clearTimeout(settleTimerRef.current);
    // eslint-disable-next-line no-undef
    settleTimerRef.current = window.setTimeout(() => {
      isClickScrollingRef.current = false;
    }, SCROLL_SETTLE_DELAY);
  };

  const handleWeekClick = (weekNumber) => {
    setActiveWeekNumber(weekNumber);
    isClickScrollingRef.current = true;
    releaseClickLock();
    getElement(weekNumber)?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  useEffect(() => {
    const scrollContainer = getElement(weeks?.[0]?.weekNumber)?.closest(
      '.ant-drawer-body',
    );
    if (!scrollContainer) return undefined;

    let frameId = null;
    const updateActiveWeek = () => {
      frameId = null;
      const containerRect = scrollContainer.getBoundingClientRect();
      const activeLine =
        containerRect.top + STICKY_TOOLBAR_HEIGHT + ACTIVE_LINE_OFFSET;
      const isAtBottom =
        scrollContainer.scrollTop + scrollContainer.clientHeight >=
        scrollContainer.scrollHeight - 2;

      let currentWeekNumber = weeks?.[0]?.weekNumber;
      weeks?.forEach((week) => {
        const rect = getElement(week?.weekNumber)?.getBoundingClientRect();
        if (!rect) return;
        if (rect.top <= activeLine) currentWeekNumber = week?.weekNumber;
      });
      if (isAtBottom) {
        const lastWeek = weeks?.[weeks.length - 1];
        const lastRect = getElement(
          lastWeek?.weekNumber,
        )?.getBoundingClientRect();
        if (lastRect && lastRect.top < containerRect.bottom) {
          currentWeekNumber = lastWeek?.weekNumber;
        }
      }
      setActiveWeekNumber(currentWeekNumber);
    };
    const handleScroll = () => {
      if (isClickScrollingRef.current) {
        releaseClickLock();
        return;
      }
      if (frameId === null) {
        // eslint-disable-next-line no-undef
        frameId = window.requestAnimationFrame(updateActiveWeek);
      }
    };

    updateActiveWeek();
    scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll);
      // eslint-disable-next-line no-undef
      if (frameId !== null) window.cancelAnimationFrame(frameId);
      // eslint-disable-next-line no-undef
      window.clearTimeout(settleTimerRef.current);
    };
  }, [weekKey]);

  useEffect(() => {
    const nav = navRef.current;
    const pill = nav?.querySelector('.roadmap-week-pill.is-active');
    if (!nav || !pill) return;
    const pillStart =
      pill.getBoundingClientRect().left -
      nav.getBoundingClientRect().left +
      nav.scrollLeft;
    const pillEnd = pillStart + pill.offsetWidth;
    if (pillStart < nav.scrollLeft) {
      nav.scrollTo({ left: pillStart - 8, behavior: 'smooth' });
    } else if (pillEnd > nav.scrollLeft + nav.clientWidth - 40) {
      nav.scrollTo({
        left: pillEnd - nav.clientWidth + 48,
        behavior: 'smooth',
      });
    }
  }, [activeWeekNumber]);

  return (
    <nav ref={navRef} className="roadmap-week-nav" aria-label="Jump to week">
      {weeks?.map((week) => {
        const { isComplete, emptyCount } = getWeekProgress(week);
        const isActive = week?.weekNumber === activeWeekNumber;
        return (
          <Tooltip
            key={week?.weekNumber}
            title={
              isComplete
                ? `Week ${week?.weekNumber} · all toys planned`
                : `Week ${week?.weekNumber} · ${emptyCount} empty slot${
                    emptyCount > 1 ? 's' : ''
                  }`
            }
          >
            <button
              type="button"
              className={`roadmap-week-pill ${isActive ? 'is-active' : ''}`}
              aria-current={isActive ? 'true' : undefined}
              onClick={() => handleWeekClick(week?.weekNumber)}
            >
              <span
                className={`roadmap-week-dot ${
                  isComplete ? 'is-complete' : 'is-incomplete'
                }`}
              />
              {`Week ${week?.weekNumber}`}
            </button>
          </Tooltip>
        );
      })}
    </nav>
  );
};

export default RoadmapWeekNav;
